import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { User } from '@sentry/angular';
import amplitude from 'amplitude-js';
import { Angulartics2Amplitude, Angulartics2OnModule } from 'angulartics2';
import { differenceInMilliseconds } from 'date-fns';
import posthog from 'posthog-js';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { FeatureNotificationComponent } from './components/feature-notification/feature-notification.component';
import { ViewAsBannerComponent } from './components/view-as-banner/view-as-banner.component';
import { Connection } from './models/connection';
import { AuthService } from './services/auth.service';
import { CompanyService } from './services/company.service';
import { ConnectionsService } from './services/connections.service';
import { PosthogService } from './services/posthog.service';
import { TablesService } from './services/tables.service';
import { UiSettingsService } from './services/ui-settings.service';
import { UserService } from './services/user.service';
import { version } from './version';

//@ts-expect-error
window.amplitude = amplitude;
amplitude.getInstance().init('9afd282be91f94da735c11418d5ff4f5');

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.css'],
	imports: [
		CommonModule,
		RouterModule,
		MatTabsModule,
		MatSidenavModule,
		MatToolbarModule,
		MatListModule,
		MatIconModule,
		MatButtonModule,
		MatBadgeModule,
		MatMenuModule,
		MatDividerModule,
		MatTooltipModule,
		Angulartics2OnModule,
		FeatureNotificationComponent,
		ViewAsBannerComponent,
	],
})
export class AppComponent {
	protected posthog = posthog;
	public isSaas = (environment as any).saas;
	public appVersion = version;
	userActivity;
	userInactive: Subject<any> = new Subject();
	currentFeatureNotificationId: string = 'edit-schema';
	isFeatureNotificationShown: boolean = false;

	userLoggedIn = null;
	isDemo = false;
	redirect_uri = `${location.origin}/loader`;
	token = null;
	routePathParam;
	authBarTheme;
	activeLink: string;
	navigationTabs: object;
	currentUser: User;
	page: string;
	whiteLabelSettingsLoaded = false;
	whiteLabelSettings: {
		logo: string;
		favicon: string;
	} = {
		logo: '',
		favicon: '',
	};
	public connections: Connection[] = [];

	constructor(
		public changeDetector: ChangeDetectorRef,
		// private ngZone: NgZone,
		public route: ActivatedRoute,
		public router: Router,
		public _connections: ConnectionsService,
		public _company: CompanyService,
		public _user: UserService,
		public _auth: AuthService,
		_tables: TablesService,
		private _uiSettings: UiSettingsService,
		angulartics2Amplitude: Angulartics2Amplitude,
		private domSanitizer: DomSanitizer,
		private matIconRegistry: MatIconRegistry,
		_posthog: PosthogService,
	) {
		this.matIconRegistry.addSvgIcon(
			'mysql',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/mysql_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'mssql',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/mssql_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'oracledb',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/oracle_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'postgres',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/postgres_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'mongodb',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/mongodb_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'dynamodb',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/dynamodb_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'ibmdb2',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/db2_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'cassandra',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/сassandra_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'cassandra-dark',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/сassandra_logo_dark.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'redis',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/redis_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'elasticsearch',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/elasticsearch_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'clickhouse',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/db-logos/clickhouse_logo.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'github',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/github.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'google',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/google.svg'),
		);
		this.matIconRegistry.addSvgIcon(
			'ai_rocket',
			this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/icons/ai-rocket.svg'),
		);
		angulartics2Amplitude.startTracking();
	}

	ngOnInit() {
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
			this.page = this.router.routerState.snapshot.url.split('?')[0];

			console.log('Navigated to page:', this.page);
		});

		const expirationDateFromURL = new URLSearchParams(location.search).get('expires');

		if (expirationDateFromURL) {
			const expirationDateString = new Date(parseInt(expirationDateFromURL, 10));
			localStorage.setItem('token_expiration', expirationDateString.toString());
		}

		let expirationToken = localStorage.getItem('token_expiration');

		if (!expirationToken) {
			this.setUserLoggedIn(false);
			this.setDefaultFavicon();
		}

		this.navigationTabs = {
			dashboard: {
				caption: 'Tables',
			},
			audit: {
				caption: 'Audit',
			},
			permissions: {
				caption: 'Permissions',
			},
			dashboards: {
				caption: 'Dashboards',
			},
			'connection-settings': {
				caption: 'Connection settings',
			},
			'edit-db': {
				caption: 'Edit connection',
			},
		};

		document.cookie = 'G_AUTH2_MIGRATION=informational';
		this._auth.cast.subscribe((res) => {
			// app initialization after user logs in
			if (!res.isTemporary && res.expires) {
				const expirationTime = new Date(res.expires);
				if (expirationTime) {
					localStorage.setItem('token_expiration', expirationTime.toISOString());
					expirationToken = expirationTime.toISOString();
				}
				this._auth.setAuthenticated(true);

				this.router.navigate(['/connections-list']);

				console.log('App component, user logged in, initializing app');
				this.initializeUserSession();

				const expirationInterval = differenceInMilliseconds(expirationTime, new Date());
				setTimeout(() => {
					this.logOut(true);
					this.router.navigate(['/login']);
				}, expirationInterval);
			}
			// app initialization if user is logged in (session restoration)
			else if (expirationToken) {
				const expirationTime = expirationToken ? new Date(expirationToken) : null;
				const currantTime = new Date();

				if (expirationTime && currantTime) {
					const expirationInterval = differenceInMilliseconds(expirationTime, currantTime);
					console.log('expirationInterval', expirationInterval);
					if (expirationInterval > 0) {
						this._auth.setAuthenticated(true);
						console.log('App component, session restoration');
						this.initializeUserSession();

						setTimeout(() => {
							if (this.userLoggedIn) this.logOut(true);
							this.router.navigate(['/login']);
						}, expirationInterval);
					} else {
						if (this.userLoggedIn) this.logOut(true);
						this.router.navigate(['/login']);
					}
				}
			}
		});

		this._user.cast.subscribe((arg) => {
			if (arg === 'delete') {
				this.logOut(true);
			}
		});
	}

	get isCustomAccentedColor() {
		return this._connections.isCustomAccentedColor;
	}

	get connectionID() {
		return this._connections.connectionID;
	}

	get currentConnectionName() {
		return this._connections.currentConnectionName;
	}

	get isTestConnection() {
		return this._connections.currentConnection?.isTestConnection || false;
	}

	get isHostedConnection() {
		return this._connections.isHostedConnection;
	}

	get visibleTabs() {
		return this._connections.visibleTabs;
	}

	get currentTab() {
		return this._connections.currentTab;
	}

	get ownUserConnections() {
		return this._connections.ownConnectionsList;
	}

	initializeUserSession() {
		this._user.fetchUser().subscribe((res: User) => {
			this.currentUser = res;
			this.isDemo = this.currentUser.email.startsWith('demo_') && this.currentUser.email.endsWith('@rocketadmin.com');
			this._user.setIsDemo(this.isDemo);
			this.setUserLoggedIn(true);
			if (typeof window.Intercom !== 'undefined')
				window.Intercom('boot', {
					...window.intercomSettings,
					user_hash: res.intercom_hash,
					user_id: res.id,
					email: res.email,
					hide_default_launcher: window.innerWidth <= 600,
				});

			if (this.isDemo)
				window.hj?.('identify', this.currentUser.id, {
					mode: 'demo',
				});

			// this._connections.fetchConnections()
			//   .subscribe((res: any) => {
			//     this.connections = res.filter(connectionItem => !connectionItem.isTestConnection);
			//   })

			this._connections.cast.subscribe(() => {
				this._connections.fetchConnections().subscribe();
			});

			this._company.getWhiteLabelProperties(res.company.id).subscribe((whiteLabelSettings) => {
				this.whiteLabelSettings.logo = whiteLabelSettings.logo;
				this.whiteLabelSettingsLoaded = true;

				if (whiteLabelSettings.favicon) {
					const newLink = document.createElement('link');
					newLink.rel = 'icon';
					newLink.href = whiteLabelSettings.favicon;
					document.head.appendChild(newLink);
				} else {
					this.setDefaultFavicon();
				}
			});
			this._uiSettings.getUiSettings().subscribe((settings) => {
				this.isFeatureNotificationShown =
					settings?.globalSettings?.lastFeatureNotificationId !== this.currentFeatureNotificationId;
			});
		});
	}

	dismissFeatureNotification() {
		this._uiSettings.updateGlobalSetting('lastFeatureNotificationId', this.currentFeatureNotificationId);
		this.isFeatureNotificationShown = false;
	}

	openSupportChat() {
		if (typeof window.Intercom !== 'undefined') {
			window.Intercom('show');
		}
	}

	setUserLoggedIn(state) {
		this.userLoggedIn = state;
		this.changeDetector.detectChanges();
	}

	logoutAndRedirectToRegistration() {
		this._auth.logOutUser().subscribe(() => {
			this.setUserLoggedIn(false);
			this.isDemo = false;
			this._user.setIsDemo(false);
			this.currentUser = null;
			localStorage.removeItem('token_expiration');
			this._auth.setAuthenticated(false);
			this.router.navigate(['/registration']);
		});
	}

	logOut(isTokenExpired?: boolean) {
		try {
			// @ts-expect-error
			google.accounts.id.revoke(this.currentUser.email, (done) => {
				console.log('consent revoked');
				console.log(done);
				console.log(this.currentUser.email);
			});
		} catch (error) {
			console.log('google error');
			console.log(error);
		}

		this._auth.logOutUser().subscribe(() => {
			this.setUserLoggedIn(null);
			localStorage.removeItem('token_expiration');
			this._auth.setAuthenticated(false);

			if (this.isSaas) {
				if (!isTokenExpired) window.location.href = 'https://rocketadmin.com/';
			} else {
				this.router.navigate(['/login']);
			}
		});
	}

	private setDefaultFavicon() {
		const faviconIco = document.createElement('link');
		faviconIco.rel = 'icon';
		faviconIco.type = 'image/x-icon';
		faviconIco.href = 'assets/favicon.ico';

		const favicon16 = document.createElement('link');
		favicon16.rel = 'icon';
		favicon16.type = 'image/png';
		favicon16.setAttribute('sizes', '16x16');
		favicon16.href = 'assets/favicon-16x16.png';

		const favicon32 = document.createElement('link');
		favicon32.rel = 'icon';
		favicon32.type = 'image/png';
		favicon32.setAttribute('sizes', '32x32');
		favicon32.href = 'assets/favicon-32x32.png';

		document.head.appendChild(faviconIco);
		document.head.appendChild(favicon16);
		document.head.appendChild(favicon32);
	}
}
